import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FocusableOption, FocusOrigin } from '@angular/cdk/a11y';
import { UiIdService } from '../../services/ui-id.service';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'lib-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
})
export class CheckboxComponent
  implements FocusableOption, ControlValueAccessor
{
  private _onChangeFn?: (value: boolean) => void;
  private _onTouchedFn?: () => void;

  hasFocus = false;
  disabled = false;

  @Input()
  id!: string;

  @Input()
  tabIndex = 0;

  @Input()
  isChecked = false;

  @Output()
  update = new EventEmitter<boolean>();

  @ViewChild('inputElement')
  inputElement!: ElementRef<HTMLInputElement>;

  constructor(
    private cdRef: ChangeDetectorRef,
    private uiIdService: UiIdService
  ) {
    this.createAutoId();
  }

  private createAutoId() {
    this.id = `checkbox_${this.uiIdService.getNextId()}`;
  }

  onFocusChange(event: FocusOrigin) {
    this.hasFocus = !!event;
    this.cdRef.markForCheck();
  }

  onChange(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.target as HTMLInputElement;
    this.update.emit(target.checked);
    this._onChangeFn?.(target.checked);
  }

  focus(origin?: FocusOrigin): void {
    this.inputElement.nativeElement.focus();
  }

  getLabel(): string {
    return this.id;
  }

  writeValue(value: boolean): void {
    this.isChecked = value;
  }

  registerOnChange(fn: any): void {
    this._onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdRef.markForCheck();
  }
}
